import { apiClient } from './apiClient';

export type JobStatus = 'pending' | 'started' | 'finished' | 'failed';
export type QueueName = 'interactive' | 'bulk' | 'benchmark';

export interface JobItem {
  job_id: string;
  queue: QueueName;
  status: JobStatus;
  func_name: string | null;
  /** Étiquette lisible : "extraction", "poll S3", "tick scheduled", ... */
  kind: string;
  /** True pour les ticks d'orchestration cross-tenant (poll, scheduled). */
  is_system: boolean;
  document_id: string | null;
  document_filename: string | null;
  user_id: string | null;
  client_id: string | null;
  client_slug: string | null;
  /** Worker exécutant le job (jobs started/finished). null pour pending. */
  worker_name: string | null;
  enqueued_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_s: number | null;
  exc_info: string | null;
}

export interface WorkerInfo {
  name: string;
  queues: string[];
  /** 'idle' | 'busy' | 'suspended' */
  state: string;
  current_job_id: string | null;
  successful_jobs: number;
  failed_jobs: number;
}

export interface JobListResponse {
  items: JobItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface QueueStats {
  name: QueueName;
  pending: number;
  started: number;
  finished: number;
  failed: number;
  paused: boolean;
  /** PROD1.4.6 #481 : pause applicative par queue (différent de paused global RQ). */
  paused_at?: string | null;
  paused_by_id?: string | null;
  paused_by_email?: string | null;
}

export interface QueuesStatusResponse {
  queues: QueueStats[];
  global_paused: boolean;
}

export interface JobStopResponse {
  job_id: string;
  queue_name: string;
  status_at_stop_attempt: string;
  stop_signal_sent: true;
  note: string;
}

export interface JobListFilters {
  queue?: QueueName;
  status?: JobStatus;
  client_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

const BASE = '/admin';

export const adminJobsService = {
  async listQueues(): Promise<QueueStats[]> {
    const r = await apiClient.get<QueueStats[]>(`${BASE}/queues`);
    return r.data;
  },

  async listWorkers(): Promise<WorkerInfo[]> {
    const r = await apiClient.get<WorkerInfo[]>(`${BASE}/workers`);
    return r.data;
  },

  async pauseQueues(): Promise<void> {
    await apiClient.post(`${BASE}/queues/pause`);
  },

  async resumeQueues(): Promise<void> {
    await apiClient.post(`${BASE}/queues/resume`);
  },

  async listJobs(filters: JobListFilters = {}): Promise<JobListResponse> {
    const params = new URLSearchParams();
    if (filters.queue) params.set('queue', filters.queue);
    if (filters.status) params.set('status', filters.status);
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.search) params.set('search', filters.search);
    params.set('limit', String(filters.limit ?? 50));
    params.set('offset', String(filters.offset ?? 0));
    const r = await apiClient.get<JobListResponse>(`${BASE}/jobs?${params.toString()}`);
    return r.data;
  },

  async cancelJob(jobId: string): Promise<void> {
    await apiClient.post(`${BASE}/jobs/${jobId}/cancel`);
  },

  async requeueJob(jobId: string): Promise<void> {
    await apiClient.post(`${BASE}/jobs/${jobId}/requeue`);
  },

  // PROD1.4.6 #481 — pause par queue (applicative, DokimoWorker)
  async pauseQueue(name: QueueName): Promise<void> {
    await apiClient.post(`${BASE}/queues/${name}/pause`);
  },

  async resumeQueue(name: QueueName): Promise<void> {
    await apiClient.post(`${BASE}/queues/${name}/resume`);
  },

  async queuesStatus(): Promise<QueuesStatusResponse> {
    const r = await apiClient.get<QueuesStatusResponse>(`${BASE}/queues/status`);
    return r.data;
  },

  // PROD1.4.6 #482 — stop running job
  async stopJob(jobId: string): Promise<JobStopResponse> {
    const r = await apiClient.post<JobStopResponse>(`${BASE}/jobs/${jobId}/stop`);
    return r.data;
  },
};
